import { FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CreditPack, DetectedFace, DetectedOriginal, Face, Original } from '../ApiTypes';
import { getCreditPacks, getFaces, getOriginals } from '../api';
import CreditPackOffer from '../components/CreditPackOffer';
import GenerateSwap from '../components/GenerateSwap';
import SetFace from '../components/setup/SetFace';
import SetOriginal from '../components/setup/SetOriginal';
import { StepContainer, StepContent, StepTitle, StepTitleBar, Visibility } from '../styles/Containers';
import { InspireMessage, LoadingMessage } from '../styles/Messages';
import { useAsyncEffect } from '../utils';
import { Trans } from 'react-i18next';

const CenterContainer = styled.div`
  @media (min-width: 560px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

const NewVideoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0px 20px;
  margin-bottom: 100px;

  @media (min-width: 800px) {
    min-width: 720px;
    max-width: 720px;
  }
`;

const GenerationTitle = styled.div`
  font-size: 26px;
  font-weight: bold;
  background: linear-gradient(to right, #ffa702, #ff5f02);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
`;

const NotActiveGenerationTitle = styled.div`
  font-size: 26px;
  font-weight: bold;
`;

const PayButton = styled.span`
  margin-top: 10px;
  border-radius: 5px;
  height: 44px;

  background-color: rgb(254, 167, 0);
  color: black;
  padding: 0px 10px;

  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: black;

  &:hover {
    background-color: #f8be53;
  }
`;

const GenerationContainer = styled.div`
`;

const BulkRow = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: center;
`;

const BulkLink = styled.button`
  padding: 0px;
  border: none;
  background: none;
  color: rgb(254, 167, 0);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

/**
 * `/library` から素材を指定して来られるようにする。
 * URL に載せることで、リロードしても戻れる（`location.state` は消える）。
 */
const usePresetSources = () => {
  const [searchParams] = useSearchParams();
  const originalUrl = searchParams.get("originalUrl");
  const faceId = searchParams.get("faceId");
  const [preset, setPreset] = useState<{ original?: Original, face?: Face }|null>(null);

  useAsyncEffect(async () => {
    if (!originalUrl && !faceId) {
      return;
    }
    // 指定されたのは保存済みの素材なので、Firestore から引くだけで足りる
    // （動画の再検出は不要）。
    const [originals, faces] = await Promise.all([
      originalUrl ? getOriginals() : Promise.resolve([]),
      faceId ? getFaces() : Promise.resolve([]),
    ]);
    setPreset({
      original: originals.find(original => original.url === originalUrl && original.detected),
      face: faces.find(face => face.faceId === faceId),
    });
  }, [originalUrl, faceId]);

  return { hasPreset: Boolean(originalUrl || faceId), preset };
};

const NewSwapPage: FC<{ original?: DetectedOriginal, face?: DetectedFace }> = (props) => {
  const { hasPreset, preset } = usePresetSources();
  const [original, setOriginal] = useState<DetectedOriginal|null>(props.original || null);
  const [face, setFace] = useState<DetectedFace|null>(props.face || null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [showPacks, setShowPacks] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getCreditPacks().then(setPacks).catch(() => setPacks([]));
  }, []);

  useEffect(() => {
    const tmpSwapId = localStorage.getItem("temporarySwap");
    if (tmpSwapId) {
      navigate(`/temporary_swaps/${tmpSwapId}`);
    }
  }, []);

  useEffect(() => {
    if (preset?.original) {
      setOriginal(preset.original);
    }
    if (preset?.face) {
      setFace(preset.face);
    }
  }, [preset]);

  // 素材を指定して来た場合は、引けるまで待つ。子は初期値をマウント時に読むため、
  // 先に描いてしまうと選択済みの状態を渡せない。
  if (hasPreset && !preset) {
    return <CenterContainer><NewVideoContainer><LoadingMessage /></NewVideoContainer></CenterContainer>;
  }

  const initialFace = preset?.face ?? props.face;
  const initialOriginal = preset?.original ?? props.original;

  return <CenterContainer>
    <NewVideoContainer>
        <StepContainer>
          <StepTitle>
            <Trans>Face</Trans><StepTitleBar />
          </StepTitle>
          <StepContent>
            <SetFace initialFace={initialFace} fromHistory={Boolean(preset?.face)} onChange={value => setFace(value)} />
          </StepContent>
        </StepContainer>
      
      <Visibility visible={face != null}>
        <StepContainer>
          <StepTitle>
            <Trans>Video</Trans><StepTitleBar />
          </StepTitle>
          <StepContent>
            <SetOriginal initialOriginal={initialOriginal} fromHistory={Boolean(preset?.original)} onChange={original => setOriginal(original)} />
          </StepContent>
        </StepContainer>
      </Visibility>

      {original && face &&
        <GenerationContainer>
          <GenerationTitle>
            <Trans>Generation</Trans>
          </GenerationTitle>
          <StepContent>
            <InspireMessage>
              <Trans>Make your dream come true right away⚡️</Trans><br/>
              <Trans>Just wait 10 seconds to start streaming video.</Trans>
            </InspireMessage>
            <GenerateSwap original={original} face={face}/>
            {/* 生成ボタンより後に置く。まとめ買いを先に見せて単発の手を止めたくない。 */}
            {packs.length > 0 && <BulkRow>
              <BulkLink onClick={() => setShowPacks(true)}>
                <Trans>Buy videos in bulk</Trans>
              </BulkLink>
            </BulkRow>}
          </StepContent>
        </GenerationContainer>
      }

      {showPacks && <CreditPackOffer packs={packs} onClose={() => setShowPacks(false)} />}
    </NewVideoContainer>
  </CenterContainer>;
};

export default NewSwapPage;
